import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { productCopyApi, type CopyStatus, type ProductCopyFilters } from '../api/productCopy';

const KEYS = {
  list:   (filters: object) => ['product-copy', 'list', filters] as const,
  detail: (id: number)      => ['product-copy', 'detail', id] as const,
};

export function useProductCopies(filters: ProductCopyFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn:  () => productCopyApi.list(filters),
    staleTime: 30_000,
  });
}

export function useProductCopy(id: number) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn:  () => productCopyApi.find(id),
    enabled:  id > 0,
  });
}

export function useGenerateProductCopy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shopifyProductId: number) => productCopyApi.generate(shopifyProductId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-copy'] });
    },
  });
}

export function useUpdateCopyStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: CopyStatus }) =>
      productCopyApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-copy'] });
    },
  });
}

export function useDeleteProductCopy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => productCopyApi.destroy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-copy'] });
    },
  });
}
